/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { AuthGuard } from './components/AuthGuard';
import { DashboardLayout } from './layouts/DashboardLayout';

// Eagerly loaded — always needed for auth flow
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';

// Lazy loaded — infrequently accessed pages
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const BooksPage = lazy(() =>
  import('./pages/BooksPage').then((m) => ({ default: m.BooksPage })),
);
const BookDetailPage = lazy(() =>
  import('./pages/BookDetailPage').then((m) => ({ default: m.BookDetailPage })),
);
const DocumentsPage = lazy(() =>
  import('./pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const BillingPage = lazy(() =>
  import('./pages/billing/BillingPage').then((m) => ({ default: m.BillingPage })),
);
const TaxPage = lazy(() =>
  import('./pages/tax/TaxPage').then((m) => ({ default: m.TaxPage })),
);
// Dashboard pages (protected)
import { ChatPage } from './pages/chat/ChatPage';
import SettingsPage from './pages/settings/SettingsPage';

const lazyFallback = (
  <div className="flex h-screen items-center justify-center bg-surface-50">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

export const router = createBrowserRouter([
  // Public auth routes
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    path: '/forgot-password',
    element: <Suspense fallback={lazyFallback}><ForgotPasswordPage /></Suspense>,
  },
  {
    path: '/reset-password',
    element: <Suspense fallback={lazyFallback}><ResetPasswordPage /></Suspense>,
  },
  {
    path: '/verify-email',
    element: <Suspense fallback={lazyFallback}><VerifyEmailPage /></Suspense>,
  },

  // Protected routes
  {
    element: <AuthGuard />,
    children: [
      // Dashboard (with sidebar layout)
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/chat" replace />,
          },
          { path: 'chat', element: <ChatPage /> },
          { path: 'chat/:conversationId', element: <ChatPage /> },
          {
            path: 'books',
            element: <Suspense fallback={lazyFallback}><BooksPage /></Suspense>,
          },
          {
            path: 'books/:id',
            element: <Suspense fallback={lazyFallback}><BookDetailPage /></Suspense>,
          },
          {
            path: 'documents',
            element: <Suspense fallback={lazyFallback}><DocumentsPage /></Suspense>,
          },
          {
            path: 'reports',
            element: <Suspense fallback={lazyFallback}><ReportsPage /></Suspense>,
          },
          {
            path: 'tax',
            element: <Suspense fallback={lazyFallback}><TaxPage /></Suspense>,
          },
          { path: 'settings', element: <SettingsPage /> },
          {
            path: 'billing',
            element: <Suspense fallback={lazyFallback}><BillingPage /></Suspense>,
          },
        ],
      },
    ],
  },
]);
